import type { SessionSlot } from '@features/customization-session/types';

import { Undo2 } from 'lucide-react';

import { Button } from '@shared/components/ui/button';
import { MediaThumbnails } from '@shared/components/media-preview';

import { SlotPdfs } from '@features/customization-session/components/slot-pdfs';
import { OrderDetails } from '@features/customization-session/components/order-details';

/** The jig, place by place: which product sits on which number. */
type Props = {
  slots: SessionSlot[];
  /** Takes a product out of the session. */
  onRemove?: (slot: SessionSlot) => void;
  /** Fetches the PDF of one of the personalizations of a product. */
  onFetchPdf?: (customization: number) => void;
  disabled?: boolean;
  /** Shows the visuals of each product (thumbnails, full size on click). */
  showVisuals?: boolean;
};

export const JigGrid = ({ slots, onRemove, onFetchPdf, disabled = false, showVisuals = false }: Props) => (
  <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))' }}>
    {slots.map((slot) => (
      <div className="flex flex-col gap-2 rounded-xl border border-border bg-card p-3" key={slot.number}>
        <div className="flex items-center gap-3">
          <div className="flex h-12 min-w-12 shrink-0 items-center justify-center rounded-lg bg-primary text-lg font-bold text-primary-foreground sm:text-[19px]">
            {slot.number}
          </div>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="text-sm font-bold">{slot.product}</span>
            <div className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
              <OrderDetails slot={slot} />
              {slot.express && <span className="shrink-0">· Express</span>}
            </div>
            <span className="truncate text-xs text-muted-foreground">Caisse : {slot.box ?? '—'}</span>
            <span className="truncate text-xs text-muted-foreground">Emplacement : {slot.workshop ?? '—'}</span>
          </div>
          {onRemove && (
            <Button
              aria-label={`Retirer le produit ${slot.number} de la session`}
              disabled={disabled}
              onClick={() => onRemove(slot)}
              size="icon"
              variant="ghost"
            >
              <Undo2 />
            </Button>
          )}
        </div>
        {showVisuals && <MediaThumbnails medias={slot.medias} title={`${slot.number} · ${slot.product}`} />}
        {onFetchPdf && <SlotPdfs customizations={slot.customizations} disabled={disabled} onFetch={onFetchPdf} />}
      </div>
    ))}
  </div>
);
